import React, { useState } from 'react';
import { Search, Filter, Star, ChefHat } from 'lucide-react';
import { menuCategories } from '../data/mock';

const MenuPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCategories = menuCategories.filter(category => {
    if (activeCategory === 'all') return true;
    return category.id === activeCategory;
  });

  const filteredItems = filteredCategories.flatMap(category =>
    category.items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase())
    ).map(item => ({ ...item, categoryName: category.name }))
  );

  // Image mapping for featured dishes
  const dishImages = {
    'Chikwanga': 'https://images.unsplash.com/photo-1647998270792-69ac80570183?w=300&h=200&fit=crop',
    'Foutou à la banane': 'https://images.unsplash.com/photo-1604329756574-bda1f2cada6f?w=300&h=200&fit=crop',
    'Accra de Mourue': 'https://images.unsplash.com/photo-1632859965308-d15227508c4b?w=300&h=200&fit=crop',
    'Poulet Mayo': 'https://images.unsplash.com/photo-1575950674322-3a1977724f2e?w=300&h=200&fit=crop',
    'Mafé à la Viande Bœuf': 'https://images.unsplash.com/photo-1739217744880-472f59559cc5?w=300&h=200&fit=crop',
    'Soupou Kandia': 'https://images.unsplash.com/photo-1632852576480-c10a8e19496a?w=300&h=200&fit=crop'
  };

  const getImageForDish = (dishName) => {
    return dishImages[dishName] || 'https://images.unsplash.com/photo-1604329756574-bda1f2cada6f?w=300&h=200&fit=crop';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Menu Header */}
      <section className="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="hero-content">
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Our Authentic Menu
              </h1>
              <div className="accent-line mb-6" style={{ margin: '0 0 1.5rem 0' }}></div>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Discover the rich culinary traditions of Africa and the Antilles. 
                Each dish is crafted with authentic ingredients and time-honored recipes 
                passed down through generations.
              </p>

              {/* Search and Filter */}
              <div className="mb-8">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search dishes or ingredients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-red-600 focus:border-transparent bg-white text-gray-900"
                  />
                </div>
              </div>
            </div>

            <div className="image-rounded">
              <img 
                src="https://images.unsplash.com/photo-1739217744880-472f59559cc5?w=600&h=400&fit=crop" 
                alt="Traditional North African couscous presentation"
                className="image-hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeCategory === 'all'
                  ? 'btn-primary'
                  : 'btn-secondary'
              }`}
            >
              All Categories
            </button>
            {menuCategories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeCategory === category.id
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Categories */}
      <section className="section-padding">
        <div className="container-custom">
          {searchTerm ? (
            /* Search Results */
            <div className="card-custom mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Search Results ({filteredItems.length} items)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {filteredItems.map((item, index) => (
                  <div key={index} className="menu-item group">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <img 
                          src={getImageForDish(item.name)}
                          alt={item.name}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                            {item.popular && (
                              <span className="popular-badge">Popular</span>
                            )}
                          </div>
                          <span className="price-badge">
                            CHF {item.price}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mb-2">{item.categoryName}</p>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4">{item.description}</p>
                        
                        <div className="flex space-x-2">
                          <button className="btn-primary text-xs py-2 px-4">
                            Add to Uber Eats
                          </button>
                          <button className="btn-secondary text-xs py-2 px-4">
                            Call to Order
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Category View */
            <div className="space-y-16">
              {filteredCategories.map(category => (
                <div key={category.id} className="menu-category">
                  <div className="card-custom">
                    <div className="flex items-center space-x-6 mb-10">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <ChefHat size={32} className="text-red-600" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">{category.name}</h2>
                        <p className="text-gray-600">{category.items.length} authentic dishes</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {category.items.map((item, index) => (
                        <div key={index} className="menu-item group">
                          <div className="flex gap-4">
                            <div className="flex-shrink-0">
                              <img 
                                src={getImageForDish(item.name)}
                                alt={item.name}
                                className="w-24 h-24 object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-3">
                                  <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                                  {item.popular && (
                                    <span className="popular-badge">Popular</span>
                                  )}
                                </div>
                                <span className="price-badge">
                                  CHF {item.price}
                                </span>
                              </div>
                              <p className="text-gray-600 text-sm leading-relaxed mb-4">{item.description}</p>
                              
                              {/* Order Buttons */}
                              <div className="flex space-x-3">
                                <button className="btn-primary text-xs py-2 px-4">
                                  Add to Uber Eats
                                </button>
                                <button className="btn-secondary text-xs py-2 px-4">
                                  Call to Order
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Menu Footer CTA */}
      <section className="section-padding section-light">
        <div className="container-custom">
          <div className="card-custom text-center max-w-3xl mx-auto" style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)', border: '1px solid #bbf7d0' }}>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
                <ChefHat size={32} className="text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Ready to Order?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Experience these authentic flavors today. 
              Order online for delivery or call us for pickup and reservations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button 
                className="btn-primary"
                onClick={() => window.open('https://www.ubereats.com', '_blank')}
              >
                Order on Uber Eats
              </button>
              <button 
                className="btn-accent"
                onClick={() => window.open('https://www.just-eat.ch', '_blank')}
              >
                Order on Just Eat
              </button>
              <button 
                className="btn-secondary"
                onClick={() => window.location.href = 'tel:079913542'}
              >
                Call to Order
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MenuPage;